import { memo, useCallback, useState } from "react";

import { WidgetEditProps } from "./types";

import { Button } from "ui/components/Button/Button";
import { classNames, useTranslate } from "helpers";

import { CheckBox } from "ui/components/CheckBox";
import { AvailableWidget } from "ui/components/Grid/types";
import { GridTemplate, GRID_TEMPLATES } from "ui/components/Grid/templates";
import { Modal } from "ui/components/Modal";
import ModalHeader from "ui/components/Modal/ModalHeader";
import ModalBody from "ui/components/Modal/ModalBody";
import { CONTACT_WIDGET_PAGE_TITLE } from "ui/components/PageTitle/consts";

const WidgetEditCard: React.FC<WidgetEditProps> = ({
  show,
  availableWidgets,
  userWidgets,
  setUserWidgets,
  template,
  onTemplateChange,
  onSuccess,
  onClose,
}) => {
  const { t } = useTranslate();

  const [localUserWidgets, setLocalUserWidgets] =
    useState<string[]>(userWidgets);
  const [localTemplate, setLocalTemplate] = useState<GridTemplate>(template);

  const handleUserWidgetClick = useCallback((widget: AvailableWidget) => {
    setLocalUserWidgets((prev) =>
      prev.includes(widget.id)
        ? prev.filter((id) => id !== widget.id)
        : [...prev, widget.id]
    );
  }, []);

  const handleTemplateChange = useCallback((newTemplate: GridTemplate) => {
    setLocalTemplate(newTemplate);
  }, []);

  const handleSave = useCallback(() => {
    setUserWidgets(localUserWidgets);
    if (localTemplate !== template) {
      onTemplateChange(localTemplate);
    }
    if (onSuccess) onSuccess();
  }, [localUserWidgets, localTemplate, template, onSuccess, setUserWidgets, onTemplateChange]);

  return (
    <Modal
      show={show}
      onHide={onClose}
      size="small"
      position="side"
    >
      <ModalHeader
        icon="/media/images/add-icon.svg"
        title={CONTACT_WIDGET_PAGE_TITLE.text}
        onHide={onClose}
      />
      <ModalBody>
        {show && (
          <div className="flex gap-4 h-screen w-full m-auto">
            {/* min-w-0 — bez toga red s predlošcima nametne svoju širinu i popis modula
                (s checkboxovima) izađe izvan modala. */}
            <form noValidate className="text-left flex-1 min-w-0 p-4">
              <div className="mb-4">
                <p className="text-sm font-medium text-slate-700 mb-2">Layout</p>
                <div className="flex gap-2 flex-wrap">
                  {GRID_TEMPLATES.map((tpl) => (
                    <button
                      key={tpl.id}
                      type="button"
                      onClick={() => handleTemplateChange(tpl.id)}
                      className={classNames(
                        "w-16 h-12 border-2 rounded p-1 flex flex-col gap-0.5 cursor-pointer transition-colors",
                        localTemplate === tpl.id
                          ? "border-blue-500 bg-blue-50"
                          : "border-gray-300 bg-white hover:border-gray-400"
                      )}
                    >
                      {Array.from({ length: 2 }).map((_, rowIndex) => (
                        <div key={rowIndex} className="flex gap-0.5 flex-1">
                          {tpl.widths.map((w, colIndex) => (
                            <div
                              key={colIndex}
                              className={classNames(
                                "rounded-sm border",
                                localTemplate === tpl.id
                                  ? "border-blue-400 bg-blue-200"
                                  : "border-gray-300 border-dashed bg-gray-100"
                              )}
                              style={{ flex: w }}
                            />
                          ))}
                        </div>
                      ))}
                    </button>
                  ))}
                </div>
              </div>

              <div className="">
                {availableWidgets.map((widget) => {
                  const isActive = localUserWidgets.includes(widget.id);

                  return (
                    <div
                      key={widget.id}
                      className="flex items-center justify-between p-3 border mb-[-1px]"
                    >
                      <span className="text-sm">{widget.name}</span>

                      <CheckBox
                        name={`widget-${widget.id}`}
                        checked={isActive}
                        onChange={() => handleUserWidgetClick(widget)}
                      />
                    </div>
                  );
                })}
              </div>

              <div className="px-4 py-3 fixed left-0 bottom-0 bg-slate-50 border border-t-slate-200 w-full flex justify-end gap-3">
                <Button variant="outlined" onClick={onClose}>
                  {t("admin.close")}
                </Button>
                <Button variant="primary" onClick={handleSave}>
                  {t("admin.save")}
                </Button>
              </div>
            </form>
          </div>
        )}
      </ModalBody>
    </Modal>
  );
};

export default memo(WidgetEditCard);
